import { useCallback, useEffect, useMemo, useState } from 'react';

import { debounce } from 'lodash';
import { get } from 'helpers/api';
import { getDefaults, serializeFilters } from './filters/helpers';

import type { Activity, PaginationData } from './types';
import type { ApiFilters, Filters } from './filters/types';

export type Paths = {
  index?: string;
}

// For reuse in the event listener setup
const refreshEventName = 'refreshActivities';
const defaultFilters = getDefaults();

export function refreshActivities() {
  window.dispatchEvent(new CustomEvent(refreshEventName));
}

export function useActivitiesState(
  { activities: externalActivities, paginate, paths, onPageDataChange, onChange }: {
    activities?: Activity[],
    paths?: Paths,
    paginate?: boolean,
    onPageDataChange?: (pageData: PaginationData) => void,
    onChange?: (_: Activity[]) => void
  } = {}
) {
  const [activities, setActivities] = useState(externalActivities || []);
  const [loaded, setLoaded] = useState(!!externalActivities || !paths?.index);
  const [loading, setLoading] = useState(!loaded);
  const [filters, _setFilters] = useState<Filters>(defaultFilters);
  const [pageData, setPageData] = useState<PaginationData | null>(null);

  function handleResponse(response) {
    setLoading(false);
    setActivities(response.activities);

    if (response.meta) {
      setPageData(response.meta)

      if (onPageDataChange) {
        onPageDataChange(response.meta as PaginationData);
      }
    }
  }

  const reloadActivities = async () => {
    if (paths?.index) {
      setLoading(true);
      const response = await get(paths.index);
      handleResponse(response)
      setLoaded(true);
    }
  };

  useEffect(() => {
    (async () => {
      if (paths?.index) {
        if (!loaded) {
          reloadActivities();
        }

        window.addEventListener('refreshActivities', reloadActivities);

        return () => {
          window.removeEventListener('refreshActivities', reloadActivities);
        };
      }
    })();
  }, [paths?.index]);

  useEffect(() => {
    // Currently, the activities component handles editing and deletion, but not adding an activity.
    // On deals, activity creation is handled externally, and the activities are also stored in the
    // deal's state. While we need to be able to ingest those added activities here, we need to
    // avoid infinite rendering loops triggered by this component exposing a change to the deal,
    // which updates it's store, which feeds that new object back in here, etc. Since the only thing
    // that can happen externally is creation, just checking length for a change is sufficient.
    // NOTE: This is weird but should be able to be cleaned up when activity creating moves out of
    //       deal code and into this standalone activity module
    if (externalActivities && externalActivities.length !== activities.length) {
      setActivities(externalActivities);
    }
  }, [externalActivities]);

  function changeActivities(changeFunction) {
    setActivities((activities) => {
      const newActivities = changeFunction(activities);

      if (onChange) {
        onChange(newActivities);
      }

      return newActivities;
    });
  }

  const addActivity = useCallback((savedActivity) => {
    changeActivities(activities => [savedActivity, ...activities]);
  }, []);

  const replaceActivity = useCallback((savedActivity) => {
    changeActivities(activities => activities.map((activity) => {
      return activity.id === savedActivity.id ? savedActivity : activity;
    }));
  }, []);

  const removeActivity = useCallback((removedActivity) => {
    changeActivities(activities => activities.filter((activity) => {
      return activity.id !== removedActivity.id;
    }));
  }, []);

  const setFilters = useCallback(async (filters: Filters, debounce = false) => {
    _setFilters(filters);

    if (paths?.index) {
      setLoading(true);
      const performSearch = debounce ? debouncedSearch : searchActivities;
      performSearch(paths.index, serializeFilters(filters));
    }
  }, [paths?.index]);

  const changeFilters = (changes: Partial<Filters>, debounce = false) => {
    const changedFilters = { ...filters, page: 1, ...changes };
    setFilters(changedFilters, debounce);
  };

  const debouncedSearch = debounce(searchActivities, 500);
  async function searchActivities(path: string, filters: ApiFilters) {
    if (!paginate) {
      delete filters.page;
    }

    const response = await get(path, { params: filters });
    handleResponse(response)
  }

  return {
    activities,
    loading,
    pageData,
    addActivity,
    replaceActivity,
    removeActivity,
    filters,
    changeFilters,
  };
}
